import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { CNYProps, FormInputs } from "../../../../../../types/walletTypes";
import { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  formatCurrencyAmount,
  getCountryName,
} from "../../../../../../utils/currency";
import { SuccessAlertContext } from "../../../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../../../context/NetworkErrorAlert";
import { useNavigate } from "react-router-dom";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { cnyTypeOfpaymentMethod, PAYMENT_METHODS } from "../../../../const";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../../utils/encryptionAlgorithm";
import { payoutRequestV2, setLoading } from "../../../../redux/slice";
import { clearUserData, setStep } from "../../../../redux/formDataSlice";
import { requestPayoutV2Otp } from "../../../../api";
import { formatValue, getErrorMessage } from "../../../../utils";
import {
  ButtonsWrapper,
  ContentWrapper,
  DetailsBox,
  DetailsContent,
  DetailsRow,
  DetailsSectionHeader,
  DetailsValue,
  EditButton,
  SectionTitle,
} from "../../styles";
import Loader from "../../../../../../components/common/Loader";
import WalletOTP from "../../../../components/SendFunds/components/WalletOTP";
import { DetailsLabel } from "../../../../../Settlements/SettlementPayout/styles";
import { Button } from "../../../../../../components/common/Button";

const Review = ({ handlePrevious }: CNYProps) => {
  const getSavedData = useAppSelector((state) => state.formData.cnyData);
  const [verifyOTP, setVerifyOTP] = useState(false);
  const { businessId } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);
  const { loading } = useAppSelector((state) => state.wallets || {});
  const isB2B = getSavedData?.paymentMethod === "bank_account_business";
  const isBankAccountIndividual = getSavedData?.paymentMethod === "bank_account_individual";
  const currencyCode = "CNY"
  const sourceCurrency = getSavedData?.sourceCurrency || "USD";
  const paymentMethodLabel =
    cnyTypeOfpaymentMethod.find(
      (method) => method.value === getSavedData?.paymentMethod,
    )?.name || formatValue(getSavedData?.paymentMethod || "");


  const senderFullName = isB2B
    ? `${getSavedData.senderBusinessRegisteredName}`
    : `${getSavedData?.senderFirstName} ${getSavedData?.senderLastName}`;
  const beneficiaryFullName = isB2B
    ? `${getSavedData?.receiverBusinessRegisteredName}`
    : `${getSavedData.receiverFirstName} ${getSavedData.receiverLastName}`;
  const totalAmount = getSavedData.totalAmount;

  const currentDate = dayjs().format("YYYY-MM-DD");

  const senderCountry = getCountryName(getSavedData?.senderAddress.countryCode);

  const { onShowAlert: onShowSuccessAlert } =
    useContext(SuccessAlertContext) || {};
  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const publicKey = keys?.data?.publicKey;

  const buildBasePayload = (data: FormInputs) => ({
    bankCode: data.bankCode,
    bankName: data.bankName,
    creditAccountCountry: "CN",
    quotationId: data.quotationId,
    receiverFirstName: data.receiverFirstName,
    receiverLastName: data.receiverLastName,
    requestId: data.reference,
    senderBusinessRegisteredName: data.senderBusinessRegisteredName,
    senderBusinessAddress: data.senderAddress,
  });

  const buildUnionPayPayload = (data: FormInputs) => {
    const { senderBusinessAddress, ...basePayload } = buildBasePayload(data);

    return {
      ...basePayload,
      cardHolderName: `${data.accountName.firstName} ${data.accountName.lastName}`,
      cardNumber: data.cardNumber.replace(/\s+/g, ""),
      purpose: data.purpose,
      senderFirstName: data.senderFirstName,
      senderLastName: data.senderLastName,
      senderIdNumber: data.senderIdNumber,
      senderIdType: data.senderIdType,
      senderNationality: "CN",
      senderAddress: data.senderAddress,
    };
  };

  const buildBankAccountIndividualPayload = (data: FormInputs) => {
    const { senderBusinessAddress, ...basePayload } = buildBasePayload(data);

    return {
      ...basePayload,
      accountName: data.bankAccountName,
      accountNumber: data.accountNumber,
      accountType: data.accountType.toUpperCase(),
      purpose: data.purpose,
      receiverFirstName: data.receiverFirstName,
      receiverLastName: data.receiverLastName,
      receiverIdNumber: data.receiverIdNumber,
      receiverRelationship: data.receiverRelationship,
      receiverIdType: data.receiverIdType,
      receiverMobileNumber: data.receiverMobileNumber,
      senderNationality: data.senderAddress?.countryCode,
      senderAddress: data.senderAddress,
      senderIdNumber: data.senderIdNumber,
      senderIdType: data.senderIdType,
      senderFirstName: data.senderFirstName,
      senderLastName: data.senderLastName,
    };
  };

  const buildAlipayPayload = (data: FormInputs) => ({
    creditAccountCountry: "CN",
    quotationId: data.quotationId,
    requestId: data.reference,
    receiverFirstName: data.receiverFirstName,
    receiverLastName: data.receiverLastName,
    receiverIdNumber: data.receiverIdNumber,
    receiverRelationship: data.receiverRelationship,
    accountNumber: data.receiverMobileNumber.replace("+", ""),
    accountId: "MOBILE",
    senderFirstName: data.senderFirstName,
    senderLastName: data.senderLastName,
    senderNationality: "CN",
    senderBirthDate: data.senderBirthDate,
    senderIdNumber: data.senderIdNumber,
    senderIdType: data.senderIdType,
    senderAddress: data.senderAddress,

    purpose: data.purpose,
  });

  const buildWeChatPayload = (data: FormInputs) => ({
    creditAccountCountry: "CN",
    quotationId: data.quotationId,
    requestId: data.reference,
    receiverFirstName: data.receiverFirstName,
    receiverLastName: data.receiverLastName,
    receiverRelationship: data.receiverRelationship,
    accountNumber: data.receiverMobileNumber?.replace("+", ""),
    accountId: "MOBILE",
    senderFirstName: data.senderFirstName,
    senderLastName: data.senderLastName,
    senderNationality: "CN",
    senderBirthDate: data.senderBirthDate,
    senderIdNumber: data.senderIdNumber,
    senderIdType: data.senderIdType,
    senderAddress: data.senderAddress,
    purpose: data.purpose,
  });

  const buildBankAccountBusinessPayload = (data: FormInputs) => ({
    ...buildBasePayload(data),
    accountName: data.bankAccountName,
    accountNumber: data.accountNumber,
    accountType: "COMPANY",
    purpose: data.purpose,
    receiverBusinessIdNumber: data.receiverBusinessIdNumber,
    receiverBusinessIdType: data.receiverBusinessIdType,
    receiverBusinessRegisteredName: data.receiverBusinessRegisteredName,
    receiverBusinessMobileNumber: data.receiverMobileNumber,
    receiverBusinessAddress: data.receiverBusinessAddress,
    document: data.document,
    website: data.website,
  });

  const handlePayout = async (otp?: string) => {
    let updatedPayload;

    switch (getSavedData.paymentMethod) {
      case PAYMENT_METHODS.unionpay:
        updatedPayload = buildUnionPayPayload(getSavedData);
        break;
      case PAYMENT_METHODS.bank_account_individual:
        updatedPayload = buildBankAccountIndividualPayload(getSavedData);
        break;

      case PAYMENT_METHODS.bank_account_business:
        updatedPayload = buildBankAccountBusinessPayload(getSavedData);
        break;
      case PAYMENT_METHODS.alipay:
        updatedPayload = buildAlipayPayload(getSavedData);
        break;
      case PAYMENT_METHODS.wechatpay:
        updatedPayload = buildWeChatPayload(getSavedData);
        break;
    }

    if (!businessId) {
      onShowErrorAlert?.("", "Business information is not available. Please try again.");
      return;
    }

    try {
      const { encryptedMessage, publicKey: jitPublicKey } = await encryptPayloadWithMerchantKeyJIT(
        businessId,
        { ...updatedPayload, otp },
      );
      const finalPayload = { message: encryptedMessage };
      const token = jitPublicKey || publicKey;

      const action = await dispatch(
        payoutRequestV2({ data: finalPayload, token }),
      );

      if (payoutRequestV2.fulfilled.match(action)) {
        onShowSuccessAlert?.("Payout successful");
        dispatch(clearUserData());
        navigate("/wallets");
      } else if (payoutRequestV2.rejected.match(action)) {
        onShowErrorAlert?.("", action.payload as string);
      }
    } catch (err: any) {
      const message = getErrorMessage(err, "Failed to initiate payout");
      onShowErrorAlert?.("", message);
    }
  };

  const handleSendOTP = async () => {
    try {
      dispatch(setLoading(true));
      await requestPayoutV2Otp(getSavedData?.reference, publicKey);
      setVerifyOTP(true);
    } catch (error) {
      onShowErrorAlert("", getErrorMessage(error, "Failed to send OTP"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePrevStep = (index: number) => {
    dispatch(setStep(index));
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      {verifyOTP && (
        <WalletOTP
          setVerifyOTP={setVerifyOTP}
          makePayment={handlePayout}
          mode="transaction"
          onResend={handleSendOTP}
        />
      )}

      <DetailsBox>
        <DetailsSectionHeader>
          <SectionTitle>Transaction details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(0)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Amount sent</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(currencyCode, getSavedData?.amount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Exchange rate</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, getSavedData?.rate)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Processing fee</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, getSavedData?.fee)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Total amount charged</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, totalAmount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Purpose</DetailsLabel>
            <DetailsValue>{formatValue(getSavedData?.purpose)}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Payment method</DetailsLabel>
            <DetailsValue>{paymentMethodLabel}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Date</DetailsLabel>
            <DetailsValue>{currentDate}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Sender details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(1)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Sender type</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.receiverAccountType)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Sender name</DetailsLabel>
            <DetailsValue>{senderFullName}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Sender country</DetailsLabel>
            <DetailsValue>{senderCountry}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Beneficiary's details</SectionTitle>
          <EditButton onClick={() => handlePrevStep(2)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>
        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary type</DetailsLabel>
            <DetailsValue>
              {formatValue(getSavedData?.accountType)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Beneficiary name</DetailsLabel>
            <DetailsValue>{beneficiaryFullName}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Phone number</DetailsLabel>
            <DetailsValue>{getSavedData?.receiverMobileNumber}</DetailsValue>
          </DetailsRow>

          {isBankAccountIndividual && (
            <DetailsRow>
              <DetailsLabel>Account number</DetailsLabel>
              <DetailsValue>{getSavedData?.accountNumber}</DetailsValue>
            </DetailsRow>
          )}
        </DetailsContent>
      </DetailsBox>

      <ButtonsWrapper>
        <Button
          onClick={handlePrevious}
          theme="alternate"
          height="48px"
          width="203px"
        >
          Back
        </Button>
        <Button
          onClick={handleSendOTP}
          label="Continue"
          height="48px"
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default Review;
